import React, { useEffect, useState } from "react";
import "./styles.css";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { API_BASE_URL } from "../config/config";

const SavedForms = () => {
  const [forms, setForms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState("Date Created (Newest First)");
  const [isLoading, setIsLoading] = useState(false);

  const fetchForms = async (searchTerm = "") => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/forms?search=${encodeURIComponent(searchTerm)}`);
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();

      // Default sort: Date Created (Newest First) with fallback
      const sortedForms = data.sort((a, b) => new Date(b.dateCreated || b.dateOfLoss) - new Date(a.dateCreated || a.dateOfLoss));

      setForms(sortedForms);
    } catch (error) {
      console.error("Error fetching forms:", error);
    }
  };

  // Fetch forms initially and when searchTerm changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchForms(searchTerm);
    }, 300); // Debounce API requests

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleOrderBy = (order) => {
    setOrderBy(order);
    let sortedForms = [...forms];

    if (order === "Date Created (Newest First)") {
      sortedForms.sort((a, b) => new Date(b.dateCreated || b.dateOfLoss) - new Date(a.dateCreated || a.dateOfLoss));
    } else if (order === "Date Created (Oldest First)") {
      sortedForms.sort((a, b) => new Date(a.dateCreated || a.dateOfLoss) - new Date(b.dateCreated || b.dateOfLoss));
    } else if (order === "Date of Loss (Newest First)") {
      sortedForms.sort((a, b) => new Date(b.dateOfLoss) - new Date(a.dateOfLoss));
    } else if (order === "Date of Loss (Oldest First)") {
      sortedForms.sort((a, b) => new Date(a.dateOfLoss) - new Date(b.dateOfLoss));
    } else if (order === "A-Z") {
      sortedForms.sort((a, b) => (a.vehicleOwner || "").localeCompare(b.vehicleOwner || ""));
    }

    setForms(sortedForms);
  };

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/forms/${selectedForm._id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete form");
      setForms(forms.filter((f) => f._id !== selectedForm._id)); // Remove form from state
      console.log("Form deleted successfully");
    } catch (error) {
      console.error("Error deleting form:", error);
    } finally {
      setModalVisible(false); // Close the modal
    }
  };

  return (
    <div
      style={{
        background: "linear-gradient(237deg, rgba(39, 39, 39, 0) 70%, rgba(0, 195, 116, 0.4) 120%), #272727",
        backgroundAttachment: "fixed",
        backgroundSize: "100%",
      }}
    >
      <header>
        <Link to="/">
          <div className="logo">
            <img src="/ae.svg" alt="Logo" className="header-image" />
          </div>
        </Link>
      </header>

      <div className="containerForms">
        <div className="titleDiv">
          <h1 className="bigTitle">Saved Forms</h1>
          <p className="subTitle">Edit, preview, and delete created forms.</p>
        </div>
        <section className="form-section">
          <div className="headline-container">
            <h2 className="forms-header">Forms</h2>
            <div className="titleBottomBorder"></div>
            <input type="text" placeholder="Search for vin, date, name" className="search-bar" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>

          <div className="orderByContainer">
            <p className="orderByLabel">Sort by: </p>
            <select className="orderByDropdown" value={orderBy} onChange={(e) => handleOrderBy(e.target.value)}>
              <option value="Date Created (Newest First)">Date Created (Newest First)</option>
              <option value="Date Created (Oldest First)">Date Created (Oldest First)</option>
              <option value="Date of Loss (Newest First)">Date of Loss (Newest First)</option>
              <option value="Date of Loss (Oldest First)">Date of Loss (Oldest First)</option>
              <option value="A-Z">A-Z</option>
            </select>
          </div>

          <div className="vindatenameContainer">
            <p className="vindatenamewrites1">VIN</p>
            <p className="vindatenamewrites2">Date of Loss</p>
            <p className="vindatenamewrites3">Name</p>
            <p className="vindatenamewrites3">Date Created</p>
          </div>

          <div className="scroll-container">
            {forms.length === 0 ? (
              <p>No forms available.</p>
            ) : (
              forms.map((form) => (
                <div className="form-group" key={form._id}>
                  <div className="custom-row">
                    <div className="rowInfo">
                      <div className="infoForm">
                        <span className="custom-data-form">{form.vin}</span>
                      </div>
                      <div className="infoFormDateOfLoss">
                        <span className="custom-data-form">{form.dateOfLoss}</span>
                      </div>
                      <div className="infoFormName">
                        <span className="custom-data-form-name">{form.vehicleOwner}</span>
                      </div>
                      <span className="custom-data-form-name">{form.dateCreated ? new Date(form.dateCreated).toLocaleDateString("en-US") : "—"}</span>
                    </div>

                    <div className="custom-button-group">
                      <button
                        className="custom-delete-button"
                        onClick={() => {
                          setModalVisible(true); // Open the modal
                          setSelectedForm(form); // Set the form to be deleted
                        }}
                      >
                        <img src="/deleteButton.svg" alt="Delete" width="53" height="53" />
                      </button>
                      <button
                        className="custom-preview-button"
                        onClick={() => {
                          if (form.pdfUrl) {
                            console.log("Opening PDF URL:", form.pdfUrl); // Debugging
                            window.open(form.pdfUrl, "_blank"); // Open the latest PDF
                          } else {
                            console.error("No PDF URL available for preview");
                            alert("This form does not have an associated PDF.");
                          }
                        }}
                      >
                        <img src="/previewButtonHeader.svg" alt="Preview" width="45" height="45" />
                      </button>
                      <Link to={`/edit-form/${form._id}`}>
                        <button className="custom-edit-button">
                          <img src="/editButton.svg" alt="Edit" width="45" height="45" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
      {modalVisible && (
        <div className="confirmation-modal">
          <img className="modalImage" src="/deleteButton.svg" alt="delete" />
          <h className="modalH">Are you sure?</h>
          <p className="modalP">Do you really want to clear all the fields? This process cannot be undone.</p>
          <div className="modalButtonscontainer">
            <button className="buttonModalCancel" onClick={() => setModalVisible(false)}>
              Cancel
            </button>
            <button className="buttonModalClear" onClick={handleDelete}>
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedForms;
